import React, { useState } from "react";
import { AgGrid, DropdownButton, toast } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import {
  useDownloadReportMutation,
  GetWhetherContainsMultipleClientsDocument,
} from "~/graphql/supergraphHooks";
import {
  type ComplianceReportType,
  type GetWhetherContainsMultipleClientsQuery,
} from "~/graphql/supergraphTypes";
import { GeneratingModal } from "../GeneratingModal";
import MultipleClientExportWarningModal from "../MultipleClientExportWarningModal";
import { getFilterVars } from "../utils";
import { getGeneratingModalToastText, getReportTypeForMutation } from "./utils";
import { KeyOfExportActions, ExportActions } from "./utils/constants";

interface ExportActionsProps {
  gridApi: AgGrid.GridApi | undefined;
}

const ExportActionsComponent: React.FC<ExportActionsProps> = (props: ExportActionsProps) => {
  const { gridApi } = props;
  const [showMultipleClientExportWarningModal, setShowMultipleClientExportWarningModal] =
    useState(false);
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [downloadReportJobId, setDownloadReportJobId] = useState(-1);
  const [currentReportTypeSelected, setCurrentReportTypeSelected] = useState(
    "" as KeyOfExportActions,
  );
  const [idsAreSelectedVsUnselected, setIdsAreSelectedVsUnselected] = useState(true);
  const [toggledNodeIds, setToggledNodeIds] = useState([] as string[]);
  const [filters, setFilters] = useState<AgGrid.FilterModel>();
  const [createReportDownloadJob] = useDownloadReportMutation();
  const closeGeneratingModal = () => {
    handleDeselectCase();
    setShowGeneratingModal(false);
    setDownloadReportJobId(-1);
  };
  const onDownloadError = () => {
    toast.error(
      `An error occurred while generating the ${getGeneratingModalToastText(currentReportTypeSelected)} report.`,
    );
    handleDeselectCase();
  };
  const onReportCheckError = () => {
    toast.error(
      `An error occurred while checking data for the ${getGeneratingModalToastText(currentReportTypeSelected)} report.`,
    );
    handleDeselectCase();
  };
  const handleExportActions = (actionType: KeyOfExportActions) => {
    if (!gridApi) return;
    const selectionState =
      gridApi.getServerSideSelectionState() as AgGrid.IServerSideSelectionState;
    if (!selectionState) return;
    setIdsAreSelectedVsUnselected(!selectionState.selectAll);
    setToggledNodeIds(selectionState.toggledNodes);
    setFilters(gridApi.getFilterModel());
    setCurrentReportTypeSelected(actionType);
    checkWhetherContainsMultipleClients(
      actionType,
      !selectionState.selectAll,
      selectionState.toggledNodes,
      gridApi.getFilterModel(),
    )
      .then((containsMultiple) => {
        if (containsMultiple) {
          setShowMultipleClientExportWarningModal(true);
          return;
        }
        downloadReport(
          actionType,
          !selectionState.selectAll,
          selectionState.toggledNodes,
          gridApi.getFilterModel(),
        ).catch(() => {
          onDownloadError();
        });
      })
      .catch(() => {
        onReportCheckError();
      });
  };
  const checkWhetherContainsMultipleClients = async (
    actionType?: KeyOfExportActions,
    selectionMode?: boolean,
    ids?: string[],
    filterInfo?: AgGrid.FilterModel,
  ) => {
    try {
      const result = await client.query<GetWhetherContainsMultipleClientsQuery>({
        query: GetWhetherContainsMultipleClientsDocument,
        variables: {
          reportType: getReportTypeForMutation(actionType ?? "CaseInformation"),
          idsAreSelectedVsUnselected: selectionMode,
          caseIds: ids?.map((id) => parseInt(id)) ?? [],
          filters: getFilterVars(filterInfo),
        },

        fetchPolicy: "no-cache",
      });
      return result.data["getWhetherContainsMultipleClients"];
    } catch {
      onReportCheckError();
    }
  };
  const handleContinueOnMultipleClientExportWarningModal = () => {
    downloadReport().catch(() => {
      onDownloadError();
    });
  };
  const downloadReport = async (
    actionType?: KeyOfExportActions,
    selectionMode?: boolean,
    ids?: string[],
    filterInfo?: AgGrid.FilterModel,
  ) => {
    setDownloadReportJobId(-1);
    setShowGeneratingModal(true);
    const reportType = getReportTypeForMutation(
      actionType ?? currentReportTypeSelected,
    ) as ComplianceReportType;
    const caseIds = ids
      ? ids.map((id) => parseInt(id))
      : toggledNodeIds.map((id) => parseInt(id));
    const idsAreSelected = selectionMode ?? idsAreSelectedVsUnselected;
    const filterVars = filterInfo ? getFilterVars(filterInfo) : getFilterVars(filters);
    try {
      const { data } = await createReportDownloadJob({
        variables: {
          reportType: reportType,
          caseIds: caseIds,
          idsAreSelectedVsUnselected: idsAreSelected,
          filters: filterVars,
        },
      });
      if (data?.createComplianceReport?.status === "error") {
        onDownloadError();
        closeGeneratingModal();
      } else {
        setDownloadReportJobId(data?.createComplianceReport?.id as number);
      }
    } catch {
      onDownloadError();
      closeGeneratingModal();
    }
  };
  const handleDeselectCase = () => {
    gridApi?.deselectAll?.();
  };

  return (
    <>
      <DropdownButton label="Export" variant="primary" align="right">
        {Object.entries(ExportActions).map(([key, value]) => (
          <DropdownButton.Action
            key={key}
            onSelect={() => handleExportActions(key as KeyOfExportActions)}
          >
            {value}
          </DropdownButton.Action>
        ))}
      </DropdownButton>

      <MultipleClientExportWarningModal
        show={showMultipleClientExportWarningModal}
        onContinue={handleContinueOnMultipleClientExportWarningModal}
        handleDisplayMultipleClientDataExportModal={setShowMultipleClientExportWarningModal}
      />

      {showGeneratingModal && (
        <GeneratingModal
          showGeneratingModal={showGeneratingModal}
          closeGeneratingModal={closeGeneratingModal}
          jobId={downloadReportJobId}
          reportType={currentReportTypeSelected}
        />
      )}
    </>
  );
};

export default ExportActionsComponent;
